import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { toast } from "react-toastify";

// API
import { eopdAPI } from "../../../../services/e-opd.api";

// Read-only blocks — shared with Visit Summary page
import {
    PatientBanner,
    VisitDetailSections,
    raw,
    statusChipOf,
} from "../shared/VisitRecordUI";

// Styles
import "../OpdStepper/OpdStepper.css";
import "./PatientVisitHistory.css";

/** Maximum visits shown at once on the page (newest first). */
const MAX_VISITS = 3;

/** Sort newest first by visit date and time. */
const sortByVisitDate = (a, b) => {
    const at = `${a?.general?.visit_dt || ""} ${a?.general?.visit_time || ""}`.trim();
    const bt = `${b?.general?.visit_dt || ""} ${b?.general?.visit_time || ""}`.trim();
    return bt.localeCompare(at);
};

export default function PatientVisitHistory() {
    const navigate = useNavigate();
    const location = useLocation();

    // Patient ID and info passed via router navigation state
    const patientId = location.state?.patientId;
    const patientFromList = location.state?.patient;

    const [loading, setLoading] = useState(true);
    const [visits, setVisits] = useState([]);
    const [expanded, setExpanded] = useState(0);

    // Redirect back to search if accessed without patient state
    useEffect(() => {
        if (!patientId) {
            navigate("/e-opd/beneficiary", { replace: true });
        }
    }, [patientId, navigate]);

    useEffect(() => {
        if (!patientId) return undefined;

        let active = true;
        const fetchVisits = async () => {
            try {
                setLoading(true);
                const res = await eopdAPI.getPatientVisits(patientId);

                if (!active) return;
                if (Array.isArray(res)) {
                    setVisits(res);
                } else if (res && res.success) {
                    const list = Array.isArray(res.visits)
                        ? res.visits
                        : Array.isArray(res.data)
                            ? res.data
                            : [];
                    setVisits(list);
                } else {
                    toast.error(res?.message || "Failed to load visit history.");
                    setVisits([]);
                }
            } catch (err) {
                console.error("Error fetching patient visits:", err);
                if (active) {
                    toast.error("Failed to load visit history.");
                    setVisits([]);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchVisits();
        return () => {
            active = false;
        };
    }, [patientId]);

    // Latest MAX_VISITS visits
    const recentVisits = useMemo(
        () => [...visits].sort(sortByVisitDate).slice(0, MAX_VISITS),
        [visits]
    );

    // Patient details from first visit or router state
    const latestVisit = recentVisits[0];
    const patient = latestVisit?.patient || patientFromList || {};

    const isLatestIncomplete = Boolean(
        latestVisit && (latestVisit.completed === false || latestVisit.completed === "false" || latestVisit.completed === 0 || !latestVisit.completed)
    );

    const handleProceedToExamination = () => {
        if (!latestVisit) return;
        const targetStep = Number(latestVisit.current_step) || 1;
        localStorage.setItem("currentOpdDisplayId", latestVisit.display_id || "");
        navigate("/e-opd/registration", {
            state: {
                displayId: latestVisit.display_id,
                currentStep: targetStep,
                patient: patient,
                fromVisitHistory: true,
            },
        });
    };

    const handleAccordionChange = (index) => (_event, isExpanded) => {
        setExpanded(isExpanded ? index : false);
    };

    if (!patientId) return null;

    if (loading) {
        return (
            <Backdrop className="opd-history-backdrop" open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <Box className="opd-stepper-page">
            {/* -------- Page header -------- */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "flex-end" }}
                gap={2}
                className="opd-history-header-stack"
            >
                <Box>
                    <Box className="opd-brand-header opd-history-brand-header">
                        <Box className="opd-brand-logo-container">
                            <img
                                src="/images/e-opd-logo.png"
                                alt="e-OPD Logo"
                                className="opd-brand-logo"
                                onError={(e) => {
                                    e.currentTarget.classList.add("opd-logo-hidden");
                                }}
                            />
                        </Box>
                    </Box>
                    <Typography className="opd-history-page-title">
                        Patient Visit History
                    </Typography>
                    <Typography className="opd-history-page-subtitle">
                        Read-only record of the last {MAX_VISITS} OPD visits for this beneficiary.
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<KeyboardArrowLeftIcon />}
                    onClick={() => navigate("/e-opd/beneficiary")}
                    className="opd-history-back-btn"
                >
                    Back to Search
                </Button>
            </Stack>

            {/* -------- History sheet -------- */}
            <Paper elevation={0} className="opd-history-paper">
                {/* Common patient banner */}
                <PatientBanner patient={patient}>
                    <Stack spacing={0.75} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                        <Typography className="opd-history-patient-meta-id">
                            P-{raw(patient.id ?? patientId)}
                        </Typography>
                        <Typography className="opd-history-patient-meta-address" sx={{ textTransform: 'capitalize', textAlign: 'end' }}>
                            {raw(patient.address)}
                        </Typography>
                        <Chip
                            label={`${recentVisits.length} ${recentVisits.length === 1 ? "visit" : "visits"} shown`}
                            size="small"
                            className="opd-history-count-chip"
                            sx={{ textAlign: 'end' }}
                        />
                    </Stack>
                </PatientBanner>

                {/* Visits */}
                <Box className="opd-history-visits-content">
                    {recentVisits.length === 0 ? (
                        <Stack alignItems="center" spacing={1} className="opd-history-empty-stack">
                            <EventNoteOutlinedIcon className="opd-history-empty-icon" />
                            <Typography className="opd-history-empty-title">
                                No visits recorded
                            </Typography>
                            <Typography className="opd-history-empty-desc">
                                This beneficiary has no OPD visit history yet.
                            </Typography>
                        </Stack>
                    ) : (
                        recentVisits.map((visit, index) => {
                            const statusChip = statusChipOf(visit);
                            const isOpen = expanded === index;
                            const statusClass = visit?.completed ? "completed" : "in-progress";

                            return (
                                <Accordion
                                    key={visit.display_id || index}
                                    expanded={isOpen}
                                    onChange={handleAccordionChange(index)}
                                    disableGutters
                                    className={`opd-accordion ${isOpen ? "opd-accordion-expanded" : ""}`}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className="opd-accordion-summary"
                                    >
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            gap={{ xs: 0.75, sm: 2 }}
                                            className="opd-history-accordion-summary-row"
                                        >
                                            <Box className="opd-history-visit-summary-left">
                                                <Stack direction="row" alignItems="center" spacing={1.25}>
                                                    <Typography className="opd-accordion-title">
                                                        Visit {index + 1}
                                                    </Typography>
                                                    {index === 0 && (
                                                        <Chip
                                                            label="Latest"
                                                            size="small"
                                                            className="opd-history-latest-chip"
                                                        />
                                                    )}
                                                </Stack>
                                                <Typography className="opd-accordion-subtitle">
                                                    {raw(visit.general?.visit_datetime)} ·{" "}
                                                    {raw(visit.general?.facility_name)}
                                                </Typography>
                                            </Box>

                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1.25}
                                                flexShrink={0}
                                            >
                                                <Typography className="opd-history-display-id">
                                                    {raw(visit.display_id)}
                                                </Typography>
                                                <Chip
                                                    label={statusChip.label}
                                                    size="small"
                                                    className={`opd-history-status-chip ${statusClass}`}
                                                />
                                            </Stack>
                                        </Stack>
                                    </AccordionSummary>

                                    <AccordionDetails className="opd-accordion-details">
                                        <VisitDetailSections visit={visit} showPatient={false} />
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    )}
                </Box>

                {isLatestIncomplete && (
                    <Box className="opd-history-bottom-bar">
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleProceedToExamination}
                            className="opd-history-proceed-btn"
                        >
                            Proceed to Examination
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
